import { Injectable } from '@angular/core';
import { poe1, poe2 } from '../../game';
import { Rarity, BaseTypeTier, WeaponType, ArmourType, DefenceType, ClusterJewelTieringMode, CurrencyTieringMode, UniquesTieringMode, EndgameContent, NonSelectableEndgameTypes } from '../models/enums';
import { Presets, FilterPreset, ItemHighlight, Filter, ArmourFilter, WeaponFilter } from '../models/filter-models';
import { FilterService } from './filter.service';
import { ItemDataService } from './item-data.service';
import { CurrencyTierService } from './tiering/currency-tier.service';
import { UniquesTierService } from './tiering/uniques-tier.service';
import { ChanceBasesTierService } from './tiering/chance-bases-tier.service';
import { Tier } from '../models/item-data-models';
import { GearTierService } from './tiering/gear-tier.service';
import { ClusterJewelTierService } from './tiering/cluster-jewel-tier.service';
import { EquipmentType } from '../models/equipment-type.enum';
import { PresetsHelperService } from './presets-helper.service';
import { DropPreviewService } from './drop-preview.service';

export interface ProgressPresetEntry {
  key: Presets;
  label: string;
  level: string;
  areaLevel: number;
  img: string;
}

const ENDGAME_RANDOM_LOOT_PRESETS: ReadonlySet<Presets> = new Set([
  'presetEndGame80',
  'presetStrict90',
  'presetVeryStrict95',
]);

export function areaLevelForProgressPreset(preset: Presets, poe2Game: boolean): number {
  const entries = poe2Game ? POE2_PROGRESS_PRESETS : POE1_PROGRESS_PRESETS;
  return entries.find(p => p.key === preset)?.areaLevel ?? 1;
}

export function randomLootAreaLevelForProgressPreset(preset: Presets, poe2Game: boolean): number | null {
  if (ENDGAME_RANDOM_LOOT_PRESETS.has(preset)) return null;
  return areaLevelForProgressPreset(preset, poe2Game);
}

export interface ApplyPresetOptions {
  filter?: Filter;
  dryRun?: boolean;
}

export const POE1_PROGRESS_PRESETS: ProgressPresetEntry[] = [
  { key: 'presetEarlyCampaign1', label: 'Early Campaign', level: '1', areaLevel: 1, img: 'presetEarlyCampaign1.webp' },
  { key: 'presetMidCampaign30', label: 'Mid-Campaign', level: '40', areaLevel: 40, img: 'presetMidCampaign30.webp' },
  { key: 'presetLateCampaignEarlyMaps60', label: 'Late-Campaign', level: '60', areaLevel: 60, img: 'presetLateCampaignEarlyMaps60.webp' },
  { key: 'presetMidMaps70', label: 'Progressing Maps', level: '75', areaLevel: 75, img: 'presetMidMaps70.webp' },
  { key: 'presetEndGame80', label: 'Early Endgame', level: '90', areaLevel: 83, img: 'presetEndGame80.webp' },
  { key: 'presetStrict90', label: 'Late Endgame', level: '95+', areaLevel: 84, img: 'presetStrict90.webp' },
];

export const POE2_PROGRESS_PRESETS: ProgressPresetEntry[] = [
  { key: 'presetEarlyCampaign1', label: 'Campaign', level: '1', areaLevel: 1, img: 'presetEarlyCampaign1.webp' },
  { key: 'presetLateCampaignEarlyMaps60', label: 'Interludes', level: '55', areaLevel: 55, img: 'interludes.webp' },
  { key: 'presetMidMaps70', label: 'Progressing Maps', level: '70', areaLevel: 70, img: 'presetProgressingMaps70.webp' },
  { key: 'presetEndGame80', label: 'Early Endgame', level: '80', areaLevel: 79, img: 'presetEarlyEndgame80.webp' },
  { key: 'presetStrict90', label: 'Late Endgame', level: '90+', areaLevel: 80, img: 'presetLateEndgame90.webp' },
  { key: 'presetVeryStrict95', label: 'Very Late Endgame', level: '95+', areaLevel: 80, img: 'presetVeryStrict95.webp' },
];

export const POE1_ENDGAME_CONTENT: EndgameContent[] = [
  // Common
  EndgameContent.Abyss, EndgameContent.Breach, EndgameContent.Delirium,
  EndgameContent.Expedition, EndgameContent.Essence, EndgameContent.Ritual, EndgameContent.Temple,
  EndgameContent.UniqueMap, EndgameContent.Crafting,
  // POE1 Only
  EndgameContent.Heist, EndgameContent.Blight, EndgameContent.Legion, EndgameContent.Synthesis,
  EndgameContent.Ultimatum,
  EndgameContent.Betrayal, EndgameContent.Labyrinth, EndgameContent.Delve,
  EndgameContent.Mirage, EndgameContent.Settlers, EndgameContent.Bestiary,
  EndgameContent.Sanctum
  // EndgameContent.Harvest,
  // EndgameContent.Sanctum, EndgameContent.ReflectingMist,
];

// TODO: we probably also want to do this for poe1; like monolith/origin?
export const POE1_ENDGAME_BOSSES: EndgameContent[] = [
  EndgameContent.Conquerors,
  EndgameContent.EldritchHorrors,
  EndgameContent.MemoryGuardians,
  EndgameContent.Shaper,
  EndgameContent.Elder,
  EndgameContent.Maven,
];

export const POE2_ENDGAME_CONTENT: EndgameContent[] = [
  // Common
  EndgameContent.Abyss, EndgameContent.Breach, EndgameContent.Delirium,
  EndgameContent.Expedition, EndgameContent.Essence, EndgameContent.Ritual, EndgameContent.Temple, EndgameContent.UniqueMap, EndgameContent.Crafting,
  // POE2 Only
  EndgameContent.ChaosTrial, EndgameContent.SekhemasTrial, EndgameContent.Monolith, EndgameContent.Origin,
  EndgameContent.CorruptedNexus, EndgameContent.AzmeriSpirit,
];

@Injectable({
  providedIn: 'root'
})
export class PresetsService {
  poe1 = poe1;
  poe2 = poe2;

  progressPresets: ProgressPresetEntry[] = this.poe2 ? POE2_PROGRESS_PRESETS : POE1_PROGRESS_PRESETS;
  endgameContent: EndgameContent[] = (this.poe2 ? POE2_ENDGAME_CONTENT : POE1_ENDGAME_CONTENT)
    .filter(c => !NonSelectableEndgameTypes.includes(c));
  endgameBosses: EndgameContent[] = this.poe1 ? POE1_ENDGAME_BOSSES : [];

  levelPresetTexts: { [preset: string]: string } = this.poe1 ?  {
    'presetEarlyCampaign1': `You're starting off with a new character. You only care about the best Weapons and Armour for your class that can drop in each area. All currency is visible.`,
    'presetMidCampaign30': `You're around halfway through the campaign. You no longer care about Normal Jewellery and small gold stacks. You're looking for 4-links at least. All currency is visible.`,
    'presetLateCampaignEarlyMaps60': `You're approaching the end of the campaign, or running White maps. You only care about Divine flasks. You don't care about Basic (non-Advanced) Gear anymore. Small stacks of low-value currency are hidden.`,
    'presetMidMaps70': `You're progressing through Yellow, towards Red maps. You only care about quality flasks and Expert items. You create your own Weapon & Armour rules. Small stacks of low-value currency are hidden.`,
    'presetEndGame80': `You're running Red maps and completing your Atlas Tree. You select your own Weapon & Armour rules. Small stacks of low-value currency are hidden.`,
    'presetStrict90': `You're running 8-mod T16, Memory-Influenced T16 or Nightmare maps or doing Pinnacle Bosses. You only care about the potentially most valuable items. You only care about valuable or large stacks of currency.`,
    // 'presetVeryStrict95': `You're running 8-mod T16, Memory-Influenced T16 or Nightmare maps or doing Pinnacle Bosses. You only care about the potentially most valuable items. You only care about valuable or large stacks of currency.`,
  } : {
    'presetEarlyCampaign1': `You're starting off with a new character. Using this preset will hide Normal items not for your class, and apply highlights. You care about all types of currency.`,
    // 'presetMidCampaign30': `You're around halfway through the campaign. You don't care about Magic items which are not for your class anymore, unless you can salvage them. You care about all types of currency.`,
    'presetLateCampaignEarlyMaps60': `You're at the end of the campaign, or just starting maps. You don't care about Rare items not for your class anymore. You start using 'Favourite Gear' rules instead of the automatic 'Leveling Gear' rules. You only salvage valuable items. You care about most types of currency.`,
    'presetMidMaps70': `You're progressing the Waystone Tiers. You only care about quality flasks and Expert items. You don't salvage most items anymore. You care less about low-value currency.`,
    'presetEndGame80': `You're running Tier 15 maps. You only care about the highest quality flasks, Expert items, and lvl 17+ Skill gems and 16+ Spirit gems. You care less about low-value currency.`,
    'presetStrict90': `You're only doing the toughest content. You only care about Expert items with high Unidentified Item Tiers, Tier 15 maps, lvl 18+ Skill and Spirit Gems, no Support Gems. You only care about valuable currency.`,
    'presetVeryStrict95': `You're speedrunning the toughest content. You only care about currency worth at least a Chaos Orb, and only the most expensive Uniques. You only care about Master T4+ Magic items, no Tiered Rares. You don't care about Jewels. You only care about lvl 20 Skill and Spirit Gems.`,
  };

  poe1progressAtlasNonSsfPresetText = 'You\'re progressing Yellow and Red maps. You\'re progressively buying gear on the Trade website and thus don\'t care about Rare items. You care less about low-value currency.';

  public static specialWeaponTypes = [WeaponType.Quivers, WeaponType.Wands, WeaponType.Sceptres, WeaponType.Staves];

  poe1SocketsClassMap: Record<string, Partial<Record<Presets, string>>> = {
    'Marauder': {
      'presetEarlyCampaign1': 'RRX',
      'presetMidCampaign30': 'RRXX',
      'presetLateCampaignEarlyMaps60': 'RRRXX',
    },
    'Ranger': {
      'presetEarlyCampaign1': 'GGX',
      'presetMidCampaign30': 'GGXX',
      'presetLateCampaignEarlyMaps60': 'GGXXX',
    },
    'Witch': {
      'presetEarlyCampaign1': 'BBX',
      'presetMidCampaign30': 'BBXX',
      'presetLateCampaignEarlyMaps60': 'BBXXX',
    },
    'Duelist': {
      'presetEarlyCampaign1': 'RGX',
      'presetMidCampaign30': 'RGXX',
      'presetLateCampaignEarlyMaps60': 'RGXXX',
    },
    'Templar': {
      'presetEarlyCampaign1': 'RBX',
      'presetMidCampaign30': 'RBXX',
      'presetLateCampaignEarlyMaps60': 'RBXXX',
    },
    'Shadow': {
      'presetEarlyCampaign1': 'GGX',
      'presetMidCampaign30': 'GBXX',
      'presetLateCampaignEarlyMaps60': 'GBXXX',
    },
    'Scion': {
      'presetEarlyCampaign1': 'XXX',
      'presetMidCampaign30': 'XXXX',
      'presetLateCampaignEarlyMaps60': 'XXXXX',
    },
  };

  constructor(private filterService: FilterService, private presetHelperService: PresetsHelperService, private currencyTierService: CurrencyTierService,
    private itemDataService: ItemDataService, private chanceBasesTierService: ChanceBasesTierService, private uniquesTierService: UniquesTierService,
    private gearTierService: GearTierService, private clusterJewelTierService: ClusterJewelTierService,
    private dropPreviewService: DropPreviewService) {
  }

  setProgressPreset(preset: Presets, options?: ApplyPresetOptions) {
    const filter = options?.filter ?? this.filterService.activeFilter().filter;
    const dryRun = options?.dryRun ?? !!options?.filter;
    const previousPreset = filter.progressPreset;

    filter.progressPreset = preset;

    this.setTieringMode(preset, filter, previousPreset, dryRun);

    const excellentUniquesHighlight = filter.uniqueTierCosmetics.find(c => c.uniqueTier === 'Excellent')!;
    const goodUniquesHighlight = filter.uniqueTierCosmetics.find(c => c.uniqueTier === 'Good')!;
    const potentialUniquesHighlight = filter.uniqueTierCosmetics.find(c => c.uniqueTier === 'Potential')!;
    const restrictedUniquesHighlight = filter.uniqueTierCosmetics.find(c => c.uniqueTier === 'Restricted')!;
    const otherUniquesHighlight = filter.uniqueTierCosmetics.find(c => c.uniqueTier === 'Other')!;

    const searchingChanceBasesHighlight = filter.chanceBaseTierCosmetics.find(c => c.chanceBaseTier === 'Searching')!;
    const potentialChanceBasesHighlight = filter.chanceBaseTierCosmetics.find(c => c.chanceBaseTier === 'Potential')!;

    const eTierDivCardsHighlight = filter.divCardTierCosmetics.find(c => c.tier === Tier.EVisible)!;

    searchingChanceBasesHighlight.displayOptions.active = true;
    potentialChanceBasesHighlight.displayOptions.active = false;

    excellentUniquesHighlight.displayOptions.active = true;
    goodUniquesHighlight.displayOptions.active = true;
    potentialUniquesHighlight.displayOptions.active = true;
    restrictedUniquesHighlight.displayOptions.active = true;
    otherUniquesHighlight.displayOptions.active = false;
    filter.showVaalUniques = true;
    filter.hideAllRemainingUniques = false;

    eTierDivCardsHighlight.displayOptions.active = true;
    eTierDivCardsHighlight.displayOptions.hidden = false;

    if (preset === 'presetEarlyCampaign1' || preset === 'presetMidCampaign30' || preset === 'presetLateCampaignEarlyMaps60') {
      // Early on we don't care about Chance Bases
      searchingChanceBasesHighlight.displayOptions.active = false;
    }

    if (preset === 'presetEarlyCampaign1' || preset === 'presetMidCampaign30' || preset === 'presetLateCampaignEarlyMaps60') {
      // During the campaign we don't want to miss any uniques
      otherUniquesHighlight.displayOptions.active = true;
    }

    if (preset === 'presetVeryStrict95') {
      searchingChanceBasesHighlight.displayOptions.active = false;
      // This was the 'temple' setup, but is probably a bit overkill now?
      // goodUniquesHighlight.displayOptions.active = false;
      // potentialUniquesHighlight.displayOptions.active = false;
    }

    if (preset === 'presetStrict90' || preset === 'presetVeryStrict95') {
      filter.showSmallUniques = false;
      filter.showVaalUniques = false;
      if (!filter.ssf) {
        filter.showClassSpecificUniquesForClasses = ['None'];
      } else if (filter.classPreset) {
        filter.showClassSpecificUniquesForClasses = [filter.classPreset];
      }
    } else {
      filter.showSmallUniques = true;
      filter.showVaalUniques = true;
      if (filter.classPreset) {
        filter.showClassSpecificUniquesForClasses = [filter.classPreset];
      } else {
        filter.showClassSpecificUniquesForClasses = ['None'];
      }
    }

    if (preset === 'presetEarlyCampaign1' || preset === 'presetMidCampaign30' || preset === 'presetLateCampaignEarlyMaps60') {
      filter.cosmeticSettings.hiddenJewellerySmallAndTransparent = false;
      filter.cosmeticSettings.hiddenFlaskSmallAndTransparent = false;
    } else {
      filter.cosmeticSettings.hiddenJewellerySmallAndTransparent = true;
      filter.cosmeticSettings.hiddenFlaskSmallAndTransparent = true;
    }

    if (this.poe1) {
      if (preset === 'presetEndGame80') {
        otherUniquesHighlight.displayOptions.active = false;
        if (!filter.ssf) {
          filter.hideAllRemainingUniques = true;
        }
      } else if (preset === 'presetStrict90' || preset === 'presetVeryStrict95') {
        otherUniquesHighlight.displayOptions.active = false;
        filter.hideAllRemainingUniques = true;
        eTierDivCardsHighlight.displayOptions.hidden = true;
      }

      searchingChanceBasesHighlight.displayOptions.active = false;
      potentialChanceBasesHighlight.displayOptions.active = false;

      filter.cosmeticSettings.stackedHiddenCurrencyPromotionEnabled = false;
      filter.cosmeticSettings.stackedGeneralCurrencyNeverPromote = ['None'];

      if (preset === 'presetMidMaps70') {
        filter.cosmeticSettings.stackedHiddenCurrencyPromotionEnabled = true;
      } else if (preset === 'presetEndGame80') {
        filter.cosmeticSettings.stackedHiddenCurrencyPromotionEnabled = true;
        filter.cosmeticSettings.stackedGeneralCurrencyNeverPromote = [
          "Transmutation Shard", "Alteration Shard", "Alchemy Shard",
          'Orb of Transmutation', 'Orb of Augmentation', 'Clear Oil', 'Sepia Oil'
        ];
      } else if (preset === 'presetStrict90' || preset === 'presetVeryStrict95') {
        filter.cosmeticSettings.stackedHiddenCurrencyPromotionEnabled = true;
        filter.cosmeticSettings.stackedGeneralCurrencyNeverPromote = [
          "Transmutation Shard", "Alteration Shard", "Alchemy Shard", "Chaos Shard", "Regal Shard",
          'Scroll of Wisdom', 'Portal Scroll', 'Orb of Transmutation', 'Orb of Augmentation', 'Clear Oil', 'Sepia Oil',
        ];
      }
    } else if (this.poe2) {
      if (preset === 'presetStrict90' || preset === 'presetVeryStrict95') {
        otherUniquesHighlight.displayOptions.active = false;
        if (preset === 'presetVeryStrict95' || !filter.ssf) {
          filter.hideAllRemainingUniques = true;
        }
      }
    }

    const presets: FilterPreset = filter.ruthless
      ? this.itemDataService.getRuthlessPreset(preset)
      : this.itemDataService.getPreset(preset);

    if (presets) {
      for (const key in presets) {
        if (!presets.hasOwnProperty(key)) continue;
        const typedKey = key as keyof typeof presets;
        if ((presets[typedKey] as ItemHighlight)?.displayOptions && (filter[typedKey] as ItemHighlight)?.displayOptions) {
          (filter[typedKey] as ItemHighlight).displayOptions.active = (presets[typedKey] as ItemHighlight).displayOptions.active;
          (filter[typedKey] as ItemHighlight).displayOptions.hidden = (presets[typedKey] as ItemHighlight).displayOptions.hidden;
        } else if (typeof presets[typedKey] === 'object' && presets[typedKey] !== null && !Array.isArray(presets[typedKey])) {
          (filter as any)[typedKey] = { ...(presets[typedKey] as object) };
        } else if (Array.isArray(presets[typedKey])) {
          (filter as any)[typedKey] = [...(presets[typedKey] as any[])];
        } else {
          (filter as any)[typedKey] = presets[typedKey];
        }
      }
    }

    if (this.poe1 && (preset === 'presetEarlyCampaign1' || preset === 'presetMidCampaign30' || preset === 'presetLateCampaignEarlyMaps60')) {
      if ((filter.showLevelingGear) || (filter.weaponFilters.filter(w => w.show).length > 0 && filter.armourFilters.filter(a => a.show).length > 0)) {
          filter.hideAllOtherGear = true;
      }
    }

    if (filter.armourFilters.length > 0 || filter.weaponFilters.length > 0) {
      const { baseTypeTier, rarity, itemLevel, minTier, highlightConfig } = this.presetHelperService.initializeMyGearSettingsByProgress(filter);

      if (filter.weaponFilters.length > 0) {
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(wpn => {
          wpn.baseTypeTier = PresetsService.specialWeaponTypes.filter(w => wpn.weaponType.includes(w)).length > 0 ? BaseTypeTier.Basic : baseTypeTier;
          wpn.rarity = rarity;
          wpn.itemLevel = itemLevel;
          wpn.highlightConfig = structuredClone(highlightConfig);
          wpn.unidentifiedTier = poe2 ? minTier : undefined;
        });
      }

      if (filter.armourFilters.length > 0) {
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(armour => {
          armour.baseTypeTier = baseTypeTier;
          armour.rarity = rarity;
          armour.itemLevel = itemLevel;
          armour.highlightConfig = structuredClone(highlightConfig);
          armour.unidentifiedTier = poe2 ? minTier : undefined;
        });
      }
    }

    if (this.poe2) {
      this.presetHelperService.setPoe2JewelFilters(filter);

      if (filter.showLevelingGear) {
        // We use leveling exceptions instead of weapon & armour exceptions
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(f => {
          const containsSpecialWeapons = PresetsService.specialWeaponTypes.filter(s => f.weaponType.includes(s)).length > 0;
          if (!filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes && containsSpecialWeapons) {
            f.show = true;
          } else {
            f.show = false;
          }
        });
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(f => f.show = false);
      } else {
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(f => f.show = true);
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(f => f.show = true);
      }

      this.disableWaystoneHighlights(filter, preset === 'presetVeryStrict95', dryRun ? filter : undefined);
    }

    if (this.poe1) {
      this.presetHelperService.setPoe1Sockets(filter);

      if (preset === 'presetEarlyCampaign1' || preset === 'presetMidCampaign30' || preset === 'presetLateCampaignEarlyMaps60') {
        // We use leveling exceptions instead of weapon & armour exceptions
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(f => { if (PresetsService.specialWeaponTypes.filter(s => f.weaponType.includes(s)).length === 0) f.show = false; });
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(f => f.show = false);
      } else if (filter.progressPreset === 'presetStrict90' || filter.progressPreset === 'presetVeryStrict95') {
        // At this point we don't care about these kinds of items anymore
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(f => f.show = false);
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(f => f.show = false);
      } else if (filter.progressPreset === 'presetMidMaps70') {
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(f => f.show = true);
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(f => f.show = true);
      }

      if (!filter.ssf && preset === 'presetMidMaps70') {
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(a => a.show = false);
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(w => w.show = w.weaponType.includes(WeaponType.Quivers));
      }

      if (filter.ssf && (preset === 'presetStrict90' || preset === 'presetVeryStrict95')) {
        filter.hideOtherGemsExceptVaalGems = true;
      }
    }

    if (!dryRun) {
      this.currencyTierService.combineCurrencyTierListData(filter);
      this.filterService.updateFilter();
      if (previousPreset !== preset || this.dropPreviewService.areaLevel() === null) {
        this.dropPreviewService.setAreaLevelFromPreset(preset);
      }
    }
  }

  enableLowValueTablets(filter: Filter, enabled: boolean) {
    const precursorTabletTier = filter.customCurrencyTiers.find(t => t.name.includes('Irradiated Tablet'));

    if (enabled && precursorTabletTier?.tier === Tier.FHidden) {
      this.filterService.resetCurrencyItemTier('Irradiated Tablet');
    } else if (!enabled && precursorTabletTier?.tier !== Tier.FHidden) {
      this.filterService.updateCurrencyItemTier('Irradiated Tablet', Tier.FHidden);
    }

    const overseerPrecursorTabletTier = filter.customCurrencyTiers.find(t => t.name === 'Overseer Tablet');

    if (enabled && overseerPrecursorTabletTier?.tier === Tier.FHidden) {
      this.filterService.resetCurrencyItemTier('Overseer Tablet');
    } else if (!enabled && overseerPrecursorTabletTier?.tier !== Tier.FHidden) {
      this.filterService.updateCurrencyItemTier('Overseer Tablet', Tier.FHidden);
    }
  }

  disableWaystoneHighlights(filter: Filter, disabled: boolean, filterOverride?: Filter) {
    const otherWaystoneHighlightsTier = filter.customEquipmentTiers.find(e => e.equipmentType === EquipmentType.ItemsWaystonesOther);
    const rareWaystoneHighlightsTier = filter.customEquipmentTiers.find(e => e.equipmentType === EquipmentType.ItemsWaystonesRare);

    if (filterOverride) {
      if (disabled && otherWaystoneHighlightsTier?.tier !== Tier.EVisible) {
        const existing = filterOverride.customEquipmentTiers.find(e => e.equipmentType === EquipmentType.ItemsWaystonesOther);
        if (existing) existing.tier = Tier.EVisible;
        else filterOverride.customEquipmentTiers.push({ equipmentType: EquipmentType.ItemsWaystonesOther, tier: Tier.EVisible });
      } else if (!disabled && otherWaystoneHighlightsTier?.tier === Tier.EVisible) {
        filterOverride.customEquipmentTiers = filterOverride.customEquipmentTiers.filter(e => e.equipmentType !== EquipmentType.ItemsWaystonesOther);
      }
      if (disabled && rareWaystoneHighlightsTier?.tier !== Tier.EVisible) {
        const existing = filterOverride.customEquipmentTiers.find(e => e.equipmentType === EquipmentType.ItemsWaystonesRare);
        if (existing) existing.tier = Tier.EVisible;
        else filterOverride.customEquipmentTiers.push({ equipmentType: EquipmentType.ItemsWaystonesRare, tier: Tier.EVisible });
      } else if (!disabled && rareWaystoneHighlightsTier?.tier === Tier.EVisible) {
        filterOverride.customEquipmentTiers = filterOverride.customEquipmentTiers.filter(e => e.equipmentType !== EquipmentType.ItemsWaystonesRare);
      }
      return;
    }

    if (disabled && otherWaystoneHighlightsTier?.tier !== Tier.EVisible) {
      this.filterService.updateEquipmentItemTier(EquipmentType.ItemsWaystonesOther, Tier.EVisible);
    } else if (!disabled && otherWaystoneHighlightsTier?.tier === Tier.EVisible) {
      this.filterService.resetEquipmentItemTier(EquipmentType.ItemsWaystonesOther);
    }

    if (disabled && rareWaystoneHighlightsTier?.tier !== Tier.EVisible) {
      this.filterService.updateEquipmentItemTier(EquipmentType.ItemsWaystonesRare, Tier.EVisible);
    } else if (!disabled && rareWaystoneHighlightsTier?.tier === Tier.EVisible) {
      this.filterService.resetEquipmentItemTier(EquipmentType.ItemsWaystonesRare);
    }
  }

  setTieringMode(preset: Presets, filter?: Filter, previousPreset?: Presets | null, dryRun = false) {
    const targetFilter = filter ?? this.filterService.activeFilter().filter;
    const applyCurrencyTieringMode = this.shouldApplyPresetTieringMode(targetFilter.tieringMode === 'Custom', previousPreset, preset);
    const applyUniquesTieringMode = this.shouldApplyPresetTieringMode(targetFilter.uniquesTieringMode === 'Custom', previousPreset, preset);
    const applyClusterJewelTieringMode = this.shouldApplyPresetTieringMode(targetFilter.clusterJewelTieringMode === 'Custom', previousPreset, preset);

    if (targetFilter.ruthless) {
      if (applyCurrencyTieringMode) {
        if (preset === 'presetStrict90') {
          this.applyCurrencyTieringMode('Normal', targetFilter, dryRun);
        } else {
          this.applyCurrencyTieringMode('Early', targetFilter, dryRun);
        }
      }

      if (applyUniquesTieringMode) {
        this.applyUniquesTieringMode('Normal', targetFilter, dryRun);
      }

      if (applyClusterJewelTieringMode) {
        if (poe1) this.applyClusterJewelTieringMode('Normal', targetFilter, dryRun);
      }

      return;
    }

    if (preset === 'presetEarlyCampaign1' || preset === 'presetMidCampaign30' || preset === 'presetLateCampaignEarlyMaps60') {
      applyCurrencyTieringMode && this.applyCurrencyTieringMode('Early', targetFilter, dryRun);
      applyUniquesTieringMode && this.applyUniquesTieringMode('Normal', targetFilter, dryRun);
      if (poe1 && applyClusterJewelTieringMode) this.applyClusterJewelTieringMode('Normal', targetFilter, dryRun);
    } else if (preset === 'presetStrict90') {
      applyCurrencyTieringMode && this.applyCurrencyTieringMode('Strict', targetFilter, dryRun);
      applyUniquesTieringMode && this.applyUniquesTieringMode('Strict', targetFilter, dryRun);
      if (poe1 && applyClusterJewelTieringMode) this.applyClusterJewelTieringMode('Strict', targetFilter, dryRun);
    } else if (preset === 'presetVeryStrict95') {
      applyCurrencyTieringMode && this.applyCurrencyTieringMode('VeryStrict', targetFilter, dryRun);
      applyUniquesTieringMode && this.applyUniquesTieringMode('VeryStrict', targetFilter, dryRun);
      if (poe1 && applyClusterJewelTieringMode) this.applyClusterJewelTieringMode('Strict', targetFilter, dryRun);
    } else {
      applyCurrencyTieringMode && this.applyCurrencyTieringMode('Normal', targetFilter, dryRun);
      applyUniquesTieringMode && this.applyUniquesTieringMode('Normal', targetFilter, dryRun);
      if (poe1 && applyClusterJewelTieringMode) this.applyClusterJewelTieringMode('Normal', targetFilter, dryRun);
    }
  }

  private applyCurrencyTieringMode(mode: CurrencyTieringMode, filter: Filter, dryRun: boolean) {
    if (dryRun) filter.tieringMode = mode;
    else this.currencyTierService.setTieringMode(mode, filter);
  }

  private applyUniquesTieringMode(mode: UniquesTieringMode, filter: Filter, dryRun: boolean) {
    if (dryRun) filter.uniquesTieringMode = mode;
    else this.uniquesTierService.setTieringMode(mode, filter);
  }

  private applyClusterJewelTieringMode(mode: ClusterJewelTieringMode, filter: Filter, dryRun: boolean) {
    if (dryRun) filter.clusterJewelTieringMode = mode;
    else this.clusterJewelTierService.setTieringMode(mode, filter);
  }

  private shouldApplyPresetTieringMode(isCustom: boolean, previousPreset: Presets | null | undefined, newPreset: Presets): boolean {
    if (!isCustom) return true;
    return this.isDecreasingPreset(previousPreset, newPreset);
  }

  private isDecreasingPreset(previousPreset: Presets | null | undefined, newPreset: Presets): boolean {
    if (!previousPreset) return false;
    const previousIndex = this.progressPresets.findIndex(p => p.key === previousPreset);
    const newIndex = this.progressPresets.findIndex(p => p.key === newPreset);
    if (previousIndex === -1 || newIndex === -1) return false;
    return newIndex < previousIndex;
  }

  toggleSsf() {
    const filter = this.filterService.activeFilter().filter;

    filter.ssf = !filter.ssf;
    if (!filter.progressPreset) {
      this.setTieringMode('presetEarlyCampaign1');
    } else {
      this.setProgressPreset(filter.progressPreset);
    }

    this.filterService.updateFilter();
  }

  setClassPreset(classPreset: string, options?: ApplyPresetOptions) {
    const filter = options?.filter ?? this.filterService.activeFilter().filter;
    const dryRun = options?.dryRun ?? !!options?.filter;

    filter.classPreset = classPreset;

    if (this.poe2) this.setClassPresetsPoe2(filter);
    else this.setClassPresetsPoe1(filter);

    if (this.poe1 && !filter.ssf && filter.progressPreset === 'presetMidMaps70') {
      filter.armourFilters.forEach(a => a.show = false);
      filter.weaponFilters.forEach(w => w.show = w.weaponType.includes(WeaponType.Quivers));
    }

    if (this.poe1 && filter.progressPreset === 'presetStrict90') {
      filter.armourFilters.forEach(a => a.show = false);
      filter.weaponFilters.forEach(w => w.show = false);
    }

    if (poe2) {
      this.presetHelperService.setPoe2JewelFilters(filter);

      if (filter.showLevelingGear) {
        // We use leveling exceptions instead of weapon & armour exceptions
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(f => { if (PresetsService.specialWeaponTypes.filter(s => f.weaponType.includes(s)).length === 0) f.show = false; });
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(f => f.show = false);
      } else {
        filter.weaponFilters.filter(w => w.fromClassPreset || w.fromProgressPreset).forEach(f => f.show = filter.progressPreset !== 'presetVeryStrict95');
        filter.armourFilters.filter(a => a.fromClassPreset || a.fromProgressPreset).forEach(f => f.show = filter.progressPreset !== 'presetVeryStrict95');
      }
    }

    if (!filter.ssf && (filter.progressPreset === 'presetStrict90' || filter.progressPreset === 'presetVeryStrict95')) {
      filter.showClassSpecificUniquesForClasses = ['None'];
    } else {
      filter.showClassSpecificUniquesForClasses = [classPreset];
    }

    if (!dryRun) {
      this.chanceBasesTierService.combineTieredChanceBases(filter);
      this.uniquesTierService.combineTieredUniques(filter);

      if (classPreset) {
        this.filterService.updateFilter();
      }
    }
  }

  private setClassPresetsPoe1(filter: Filter) {
    const classPreset = filter.classPreset!;
    const { baseTypeTier, rarity, itemLevel, minTier, highlightConfig } = this.presetHelperService.initializeMyGearSettingsByProgress(filter);

    const isCampaign = filter.progressPreset === 'presetEarlyCampaign1' || filter.progressPreset === 'presetLateCampaignEarlyMaps60';

    if (isCampaign) {
      filter.showLevelingGear = true;
      filter.showLevelingGearWeapons = true;
      filter.showLevelingGearArmour = true;
    } else {
      filter.showLevelingGear = false;
      filter.showLevelingGearWeapons = false;
      filter.showLevelingGearArmour = false;
    }

    if (classPreset == 'Marauder' || classPreset == 'Duelist' || classPreset == 'Shadow' || classPreset == 'Templar') {
      filter.hideTinctures = filter.hideUtilityFlasks;
    } else {
      filter.hideTinctures = true;
    }

    this.presetHelperService.setPoe1Sockets(filter);

    if (classPreset === 'Marauder') {
      filter.showLevelingGearWeaponClasses = [WeaponType.OneHandMaces, WeaponType.TwoHandMaces, WeaponType.OneHandAxes, WeaponType.TwoHandAxes, WeaponType.OneHandSwords, WeaponType.TwoHandSwords, WeaponType.Staves, WeaponType.Warstaves, WeaponType.Sceptres];
      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Armour];

      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: !isCampaign, weaponType: [WeaponType.OneHandMaces, WeaponType.TwoHandMaces, WeaponType.OneHandAxes, WeaponType.TwoHandAxes, WeaponType.OneHandSwords, WeaponType.TwoHandSwords, WeaponType.Staves, WeaponType.Warstaves, WeaponType.Sceptres], baseTypeTier, rarity, itemLevel }),
      ];

      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: !isCampaign, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots, ArmourType.Shields], defenceType: [DefenceType.Armour], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Ranger') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Bows, WeaponType.Quivers];
      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Evasion, DefenceType.EnergyShield, DefenceType.EvasionEnergyShield];

      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: !isCampaign, weaponType: [WeaponType.Bows], baseTypeTier, rarity, itemLevel }),
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Quivers], baseTypeTier: BaseTypeTier.Basic, rarity, itemLevel }),
      ];

      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: !isCampaign, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.Evasion, DefenceType.EvasionEnergyShield, DefenceType.EnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Witch') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Wands, WeaponType.Sceptres];
      filter.showLevelingGearArmourDefenceTypes = [DefenceType.EnergyShield];

      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Wands, WeaponType.Sceptres], baseTypeTier, rarity: Rarity.Magic, itemLevel }),
      ];

      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: !isCampaign, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots, ArmourType.Shields], defenceType: [DefenceType.EnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Duelist') {
      filter.showLevelingGearWeaponClasses = [WeaponType.OneHandSwords, WeaponType.TwoHandSwords, WeaponType.OneHandAxes, WeaponType.TwoHandAxes, WeaponType.ThrustingOneHandSwords];
      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Armour, DefenceType.Evasion, DefenceType.ArmourEvasion];

      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: !isCampaign, weaponType: [WeaponType.OneHandSwords, WeaponType.TwoHandSwords, WeaponType.OneHandAxes, WeaponType.TwoHandAxes], baseTypeTier, rarity, itemLevel }),
      ];

      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: !isCampaign, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.ArmourEvasion, DefenceType.Armour, DefenceType.Evasion], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Templar') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Staves, WeaponType.Sceptres];
      filter.showLevelingGearArmourDefenceTypes = [DefenceType.EnergyShield, DefenceType.Armour, DefenceType.ArmourEnergyShield];

      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Staves, WeaponType.Sceptres], baseTypeTier, rarity: Rarity.Magic, itemLevel }),
      ];

      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: !isCampaign, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.ArmourEnergyShield, DefenceType.Armour, DefenceType.EnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Shadow') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Claws, WeaponType.Daggers, WeaponType.Bows, WeaponType.Quivers];
      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Evasion, DefenceType.EnergyShield, DefenceType.EvasionEnergyShield];

      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: !isCampaign, weaponType: [WeaponType.Claws, WeaponType.Daggers, WeaponType.Bows], baseTypeTier, rarity, itemLevel }),
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Quivers], baseTypeTier: BaseTypeTier.Basic, rarity, itemLevel }),
      ];

      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: !isCampaign, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.EvasionEnergyShield, DefenceType.Evasion, DefenceType.EnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Scion') {
      filter.showLevelingGearWeaponClasses = [WeaponType.OneHandMaces, WeaponType.TwoHandMaces, WeaponType.OneHandAxes, WeaponType.TwoHandAxes, WeaponType.OneHandSwords, WeaponType.TwoHandSwords, WeaponType.Staves, WeaponType.Warstaves, WeaponType.Sceptres];
      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Armour, DefenceType.Evasion, DefenceType.EnergyShield, DefenceType.ArmourEvasion, DefenceType.ArmourEnergyShield, DefenceType.EvasionEnergyShield];

      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: !isCampaign, weaponType: [WeaponType.OneHandMaces, WeaponType.TwoHandMaces, WeaponType.OneHandAxes, WeaponType.TwoHandAxes, WeaponType.OneHandSwords, WeaponType.TwoHandSwords, WeaponType.Staves, WeaponType.Warstaves, WeaponType.Sceptres], baseTypeTier, rarity, itemLevel }),
      ];

      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: !isCampaign, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots, ArmourType.Shields], defenceType: [DefenceType.Armour, DefenceType.Evasion, DefenceType.EnergyShield, DefenceType.ArmourEvasion, DefenceType.ArmourEnergyShield, DefenceType.EvasionEnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    }

    filter.weaponFilters.forEach(w => {
      w.highlightConfig = structuredClone(highlightConfig);
    });
    filter.armourFilters.forEach(a => {
      a.highlightConfig = structuredClone(highlightConfig);
    });
  }

  private setClassPresetsPoe2(filter: Filter) {
    const { baseTypeTier, rarity, itemLevel, minTier, highlightConfig } = this.presetHelperService.initializeMyGearSettingsByProgress(filter);
    const classPreset = filter.classPreset!;

    if (classPreset === 'Warrior') {
      filter.showLevelingGearWeaponClasses = [WeaponType.OneHandMaces, WeaponType.TwoHandMaces];
      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.OneHandMaces, WeaponType.TwoHandMaces], baseTypeTier, rarity, itemLevel }),
      ];

      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Armour];
      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots, ArmourType.Shields], defenceType: [DefenceType.Armour], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Ranger') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Bows, WeaponType.Quivers];
      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Bows], baseTypeTier, rarity, itemLevel }),
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Quivers], baseTypeTier: BaseTypeTier.Basic, rarity, itemLevel }),
      ];

      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Evasion, DefenceType.EvasionEnergyShield];
      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.Evasion, DefenceType.EvasionEnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Huntress') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Spears];
      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Spears], baseTypeTier, rarity, itemLevel }),
      ];

      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Evasion, DefenceType.EvasionEnergyShield];
      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.Evasion, DefenceType.EvasionEnergyShield], baseTypeTier, rarity, itemLevel }),
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.Bucklers], defenceType: [DefenceType.Evasion], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Witch') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Staves, WeaponType.Wands, WeaponType.Sceptres, WeaponType.Foci];
      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Staves, WeaponType.Wands, WeaponType.Sceptres], baseTypeTier: BaseTypeTier.Basic, rarity, itemLevel }),
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Foci], baseTypeTier, rarity, itemLevel }),
      ];

      filter.showLevelingGearArmourDefenceTypes = [DefenceType.EnergyShield];
      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.EnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Sorceress') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Staves, WeaponType.Wands, WeaponType.Foci];
      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Staves, WeaponType.Wands], baseTypeTier: BaseTypeTier.Basic, rarity, itemLevel }),
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Foci], baseTypeTier, rarity, itemLevel }),
      ];

      filter.showLevelingGearArmourDefenceTypes = [DefenceType.EnergyShield];
      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.EnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Mercenary') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Crossbows];
      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Crossbows], baseTypeTier, rarity, itemLevel }),
      ];

      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Armour, DefenceType.ArmourEvasion, DefenceType.Evasion];
      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.Armour, DefenceType.ArmourEvasion, DefenceType.Evasion], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Monk') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Quarterstaves];
      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Quarterstaves], baseTypeTier, rarity, itemLevel }),
      ];

      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Evasion, DefenceType.EvasionEnergyShield, DefenceType.EnergyShield];
      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.Evasion, DefenceType.EvasionEnergyShield, DefenceType.EnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    } else if (classPreset === 'Druid') {
      filter.showLevelingGearWeaponClasses = [WeaponType.Talismans, WeaponType.Staves];
      filter.weaponFilters = [
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Talismans], baseTypeTier, rarity, itemLevel }),
        new WeaponFilter({ fromClassPreset: true, show: true, weaponType: [WeaponType.Staves], baseTypeTier: BaseTypeTier.Basic, rarity, itemLevel }),
      ];

      filter.showLevelingGearArmourDefenceTypes = [DefenceType.Armour, DefenceType.ArmourEnergyShield, DefenceType.EnergyShield];
      filter.armourFilters = [
        new ArmourFilter({ fromClassPreset: true, show: true, armourType: [ArmourType.BodyArmours, ArmourType.Helmets, ArmourType.Gloves, ArmourType.Boots], defenceType: [DefenceType.Armour, DefenceType.ArmourEnergyShield, DefenceType.EnergyShield], baseTypeTier, rarity, itemLevel }),
      ];
    }

    filter.weaponFilters.forEach(w => {
      w.highlightConfig = structuredClone(highlightConfig);
      w.unidentifiedTier = minTier;
    });
    filter.armourFilters.forEach(a => {
      a.highlightConfig = structuredClone(highlightConfig);
      a.unidentifiedTier = minTier;
    });
  }
}
